import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { Play, Trophy, Dna, Loader2, Swords, Crown, TrendingUp, TrendingDown } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API_BASE as API } from '../config';
function headers() { return { Authorization: `Bearer ${localStorage.getItem("token")}` }; }

export default function TournamentPage() {
  const [status, setStatus] = useState(null);
  const [taskProgress, setTaskProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [chartData, setChartData] = useState([]);

  const fetchStatus = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/api/breeding-tournament/status`, { headers: headers() }),
        axios.get(`${API}/api/breeding-tournament/results`, { headers: headers() }),
      ]);
      setStatus(s.data);
      if (r.data && r.data.status !== "no_results" && r.data.survivors?.length > 0) {
        setResults(r.data);
        // Build chart data from survivor equity curves
        const survivors = r.data.survivors || [];
        const maxLen = Math.max(...survivors.map(s => s.equity_curve?.length || 0));
        const step = Math.max(1, Math.floor(maxLen / 400));
        const data = [];
        for (let i = 0; i < maxLen; i += step) {
          const point = { candle: i };
          for (const s of survivors) {
            const ec = s.equity_curve || [];
            const entry = ec[i] || ec[ec.length - 1];
            if (entry) point[s.name] = entry.pnl_pct;
          }
          data.push(point);
        }
        setChartData(data);
      }
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(() => {
      // Only poll if tournament is running or we don't have results yet
      if (!results) fetchStatus();
    }, 10000);
    return () => clearInterval(t);
  }, [fetchStatus, results]);

  const startTournament = async () => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/api/breeding-tournament/start?num_racers=10&days=3150&num_survivors=5&num_hybrids=10`,
        {},
        { headers: headers() }
      );
      if (r.data.task_id) {
        toast.success("Tournament launched! Fetching 8.6 years of data...");
        const pollTask = setInterval(async () => {
          try {
            const t = await axios.get(`${API}/api/tasks/${r.data.task_id}`, { headers: headers() });
            setTaskProgress({ progress: t.data.progress, description: t.data.description });
            if (t.data.status === "completed" || t.data.status === "failed") {
              clearInterval(pollTask);
              setTaskProgress(null);
              fetchStatus();
              if (t.data.status === "failed") toast.error(`Failed: ${t.data.error}`);
              else toast.success("Tournament complete!");
            }
          } catch (e) { /* ignore */ }
        }, 2000);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start");
    }
    setLoading(false);
  };

  const isRunning = status?.running;
  const phase = status?.phase || "idle";
  const survivors = results?.survivors || status?.survivors || [];
  const hybrids = results?.hybrids || status?.hybrids || [];
  const COLORS = ["#FFD700", "#FF3366", "#00E5FF", "#FF6B35", "#B266FF"];

  return (
    <div className="min-h-screen bg-black text-white p-4 space-y-4" data-testid="tournament-page">
      {/* Header */}
      <div className="flex items-center justify-between" data-testid="tournament-header">
        <div>
          <h1 className="text-xl font-black tracking-wider flex items-center gap-2">
            <Swords className="w-5 h-5 text-amber-400" />
            BREEDING TOURNAMENT
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            8.6-Year Stress Test + Survivor Breeding — Create Next-Gen Hybrid Strategies
          </p>
        </div>
        <div className="flex items-center gap-3">
          {results?.completed_at && (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40 text-xs" data-testid="tournament-complete-badge">
              COMPLETE — {results.completed_at.substring(0,16).replace("T"," ")}
            </Badge>
          )}
          {!isRunning && (
            <Button onClick={startTournament} disabled={loading} className="bg-amber-600 hover:bg-amber-500 text-black font-bold h-9 px-5 text-sm" data-testid="start-tournament-btn">
              <Play className="w-4 h-4 mr-1.5" />
              {phase === "complete" || survivors.length > 0 ? "NEW TOURNAMENT" : "START TOURNAMENT"}
            </Button>
          )}
        </div>
      </div>

      {/* Task Progress */}
      {taskProgress && (
        <div className="bg-zinc-900/80 border border-amber-700/40 rounded-sm p-3 flex items-center gap-4" data-testid="tournament-progress">
          <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
          <div className="flex-1">
            <div className="text-xs text-amber-400 font-mono mb-1">{taskProgress.description}</div>
            <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 transition-all duration-500" style={{ width: `${taskProgress.progress}%` }} />
            </div>
          </div>
          <span className="text-xs text-zinc-500 font-mono">{taskProgress.progress}%</span>
        </div>
      )}

      {/* Equity Curve Chart */}
      {chartData.length > 0 && (
        <Card className="bg-zinc-950 border-zinc-800" data-testid="tournament-chart">
          <CardContent className="p-3" style={{ height: 340 }}>
            <div className="text-[10px] text-zinc-600 font-mono mb-1">SURVIVOR EQUITY CURVES — 8.6 YEARS ($100K START)</div>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                <XAxis dataKey="candle" stroke="#555" tick={{ fontSize: 9 }} tickFormatter={(v) => {
                  const year = Math.floor(v / (365*6)) + 1;
                  return `Y${year}`;
                }} />
                <YAxis stroke="#555" tick={{ fontSize: 9 }} tickFormatter={(v) => `${v > 0 ? "+" : ""}${(v/1000).toFixed(0)}K%`} />
                <ReferenceLine y={0} stroke="#333" strokeWidth={2} />
                <Tooltip
                  contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 4, fontSize: 10 }}
                  formatter={(v, name) => [`${v > 0 ? "+" : ""}${v.toFixed(1)}%`, name]}
                  labelFormatter={(v) => `Candle ${v} (Year ${Math.floor(v / (365*6)) + 1})`}
                />
                {survivors.map((s, i) => (
                  <Line key={s.name} type="monotone" dataKey={s.name} stroke={s.color || COLORS[i % COLORS.length]} strokeWidth={i === 0 ? 2.5 : 1.5} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Survivors Panel */}
        <Card className="bg-zinc-950 border-zinc-800" data-testid="survivors-panel">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Crown className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-bold text-zinc-300 tracking-wider">TOP SURVIVORS</span>
              <Badge className="bg-zinc-800 text-zinc-400 text-[10px]">8.6-Year Race</Badge>
            </div>
            {survivors.length === 0 ? (
              <div className="text-zinc-600 text-sm py-8 text-center">Start a tournament to see survivors...</div>
            ) : (
              <div className="space-y-2">
                {survivors.map((s, i) => (
                  <div key={s.name + i} className={`p-2.5 rounded-sm border ${i === 0 ? "bg-amber-950/20 border-amber-700/50" : "bg-zinc-900/60 border-zinc-800/60"}`} data-testid={`survivor-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-black ${i === 0 ? "text-amber-400" : "text-zinc-600"}`}>#{s.rank || i + 1}</span>
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color || COLORS[i % COLORS.length] }} />
                        <span className="text-xs font-bold text-zinc-300 truncate max-w-[200px]">{s.name}</span>
                      </div>
                      <span className={`text-sm font-mono font-black ${(s.pnl_pct || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {(s.pnl_pct || 0) > 0 ? "+" : ""}{(s.pnl_pct || 0).toLocaleString(undefined, {maximumFractionDigits: 1})}%
                      </span>
                    </div>
                    <div className="mt-1 flex gap-3 text-[9px] font-mono text-zinc-500">
                      {s.sharpe && <span>Sharpe: <span className="text-cyan-400">{s.sharpe}</span></span>}
                      {s.max_drawdown_pct && <span>DD: <span className="text-orange-400">{s.max_drawdown_pct}%</span></span>}
                      {s.profit_factor && <span>PF: <span className="text-amber-400">{s.profit_factor}</span></span>}
                      {s.trades && <span>{s.trades}T WR{s.win_rate}%</span>}
                    </div>
                    {s.yearly_returns && Object.keys(s.yearly_returns).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5">
                        {Object.entries(s.yearly_returns).sort().map(([yr, pnl]) => (
                          <span key={yr} className={`text-[9px] font-mono ${pnl >= 0 ? "text-emerald-500/70" : "text-red-400/70"}`}>
                            {yr}: {pnl > 0 ? "+" : ""}{pnl}%
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hybrids Panel */}
        <Card className="bg-zinc-950 border-zinc-800" data-testid="hybrids-panel">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Dna className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-bold text-zinc-300 tracking-wider">BRED HYBRIDS</span>
              <Badge className="bg-zinc-800 text-zinc-400 text-[10px]">Gen 1</Badge>
            </div>
            {hybrids.length === 0 ? (
              <div className="text-zinc-600 text-sm py-8 text-center">Hybrids appear after breeding...</div>
            ) : (
              <div className="space-y-2">
                {hybrids.map((h, i) => {
                  const isPositive = (h.pnl_pct || 0) >= 0;
                  return (
                    <div key={h.name} className={`p-2.5 rounded-sm border ${i === 0 ? "bg-violet-950/20 border-violet-700/50" : "bg-zinc-900/60 border-zinc-800/60"}`} data-testid={`hybrid-${i}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Dna className={`w-3 h-3 ${i === 0 ? "text-violet-400" : "text-zinc-600"}`} />
                          <span className="text-xs font-bold text-zinc-300 truncate max-w-[180px]">{h.name}</span>
                        </div>
                        {h.pnl_pct !== undefined && (
                          <span className={`text-sm font-mono font-black ${isPositive ? "text-emerald-400" : "text-red-400"}`}>
                            {isPositive ? "+" : ""}{(h.pnl_pct || 0).toLocaleString(undefined, {maximumFractionDigits: 1})}%
                          </span>
                        )}
                      </div>
                      {h.parents && (
                        <div className="mt-1 text-[9px] text-zinc-500">
                          Parents: <span className="text-zinc-400">{h.parents.join(" × ")}</span>
                        </div>
                      )}
                      <div className="mt-1 flex gap-3 text-[9px] font-mono text-zinc-500">
                        {h.win_rate !== undefined && <span>WR: <span className="text-cyan-400">{h.win_rate}%</span></span>}
                        {h.max_drawdown_pct !== undefined && <span>DD: <span className="text-orange-400">{h.max_drawdown_pct}%</span></span>}
                        {h.profit_factor !== undefined && <span>PF: <span className="text-amber-400">{h.profit_factor}</span></span>}
                        {h.trades !== undefined && <span>{h.trades}T</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="text-[10px] text-zinc-600 font-mono text-center py-2">
        Tournament: Fetch 8.6Y data &rarr; Race top 10 &rarr; Select 5 survivors &rarr; Breed 10 hybrids &rarr; Backtest hybrids
      </div>
    </div>
  );
}
